import * as Q from './exact.js';
class MatError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
  code;
}
const scal = (q) => ({ t: 's', q });
function mat(a) {
  return { t: 'm', r: a.length, c: a[0]?.length ?? 0, a };
}
function identity(n, s = Q.ONE) {
  return Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_2, j) => (i === j ? s : Q.ZERO)),
  );
}
const sizeText = (v) => `${v.r} \xD7 ${v.c}`;
function asMatrix(v, n) {
  if (v.t === 'm') return v.a;
  if (v.t === 'id') return identity(n, v.s);
  throw new MatError('kind', 'Un nombre n\u2019est pas une matrice.');
}
function addVals(x, y, sign = 1) {
  const f = (a, b) => (sign === 1 ? Q.add(a, b) : Q.sub(a, b));
  if (x.t === 's' && y.t === 's') return scal(f(x.q, y.q));
  if (x.t === 's' || y.t === 's') {
    const s = x.t === 's' ? x : y;
    const o = x.t === 's' ? y : x;
    if (!Q.isZero(s.q))
      throw new MatError('kind', 'On n\u2019ajoute pas un nombre \xE0 une matrice.');
    return x.t === 's' ? (sign === 1 ? o : scaleVal(o, Q.rat(-1))) : o;
  }
  if (x.t === 'id' && y.t === 'id') return { t: 'id', s: f(x.s, y.s) };
  const n = x.t === 'm' ? x.r : y.r;
  if (x.t === 'm' && y.t === 'm' && (x.r !== y.r || x.c !== y.c))
    throw new MatError(
      'size',
      `Les tailles ne vont pas : on n\u2019ajoute pas une matrice ${sizeText(x)} et une matrice ${sizeText(y)}.`,
    );
  if ((x.t === 'id' && y.t === 'm' && y.r !== y.c) || (y.t === 'id' && x.t === 'm' && x.r !== x.c))
    throw new MatError(
      'size',
      'L\u2019identit\xE9 est carr\xE9e : on ne l\u2019ajoute qu\u2019\xE0 une matrice carr\xE9e.',
    );
  const A = asMatrix(x, n);
  const B = asMatrix(y, n);
  return mat(A.map((row, i) => row.map((a, j) => f(a, B[i][j]))));
}
function scaleVal(v, k) {
  if (v.t === 's') return scal(Q.mul(v.q, k));
  if (v.t === 'id') return { t: 'id', s: Q.mul(v.s, k) };
  return mat(v.a.map((row) => row.map((a) => Q.mul(a, k))));
}
function mulVals(x, y) {
  if (x.t === 's') return scaleVal(y, x.q);
  if (y.t === 's') return scaleVal(x, y.q);
  if (x.t === 'id' && y.t === 'id') return { t: 'id', s: Q.mul(x.s, y.s) };
  if (x.t === 'id') {
    const m = y;
    return scaleVal(m, x.s);
  }
  if (y.t === 'id') return scaleVal(x, y.s);
  if (x.c !== y.r)
    throw new MatError(
      'size',
      `Les tailles ne vont pas : une matrice ${sizeText(x)} fois une matrice ${sizeText(y)} n\u2019existe pas (le nombre de colonnes de la premi\xE8re doit \xEAtre le nombre de lignes de la seconde).`,
    );
  const out = [];
  for (let i = 0; i < x.r; i++) {
    const row = [];
    for (let j = 0; j < y.c; j++) {
      let s = Q.ZERO;
      for (let k = 0; k < x.c; k++) s = Q.add(s, Q.mul(x.a[i][k], y.a[k][j]));
      row.push(s);
    }
    out.push(row);
  }
  return mat(out);
}
function detOf(a) {
  const n = a.length;
  const m = a.map((r) => r.slice());
  let det = Q.ONE;
  for (let col = 0; col < n; col++) {
    let p = col;
    while (p < n && Q.isZero(m[p][col])) p++;
    if (p === n) return Q.ZERO;
    if (p !== col) {
      [m[p], m[col]] = [m[col], m[p]];
      det = Q.neg(det);
    }
    const piv = m[col][col];
    det = Q.mul(det, piv);
    for (let r = col + 1; r < n; r++) {
      const f = Q.div(m[r][col], piv);
      for (let k = col; k < n; k++) m[r][k] = Q.sub(m[r][k], Q.mul(f, m[col][k]));
    }
  }
  return det;
}
function inverseOf(a) {
  const n = a.length;
  const m = a.map((r, i) => [...r, ...identity(n)[i]]);
  for (let col = 0; col < n; col++) {
    let p = col;
    while (p < n && Q.isZero(m[p][col])) p++;
    if (p === n) return null;
    [m[p], m[col]] = [m[col], m[p]];
    const piv = m[col][col];
    m[col] = m[col].map((x) => Q.div(x, piv));
    for (let r = 0; r < n; r++) {
      if (r === col || Q.isZero(m[r][col])) continue;
      const f = m[r][col];
      m[r] = m[r].map((x, k) => Q.sub(x, Q.mul(f, m[col][k])));
    }
  }
  return m.map((r) => r.slice(n));
}
function powVal(v, k) {
  if (v.t === 's') {
    const q = Q.pow(v.q, k);
    if (!q) throw new MatError('div0', 'Division par 0.');
    return scal(q);
  }
  if (v.t === 'id') {
    const q = Q.pow(v.s, k);
    if (!q) throw new MatError('inverse', 'Cette matrice n\u2019est pas inversible.');
    return { t: 'id', s: q };
  }
  if (v.r !== v.c)
    throw new MatError(
      'size',
      `Seule une matrice carr\xE9e a des puissances (ici ${sizeText(v)}).`,
    );
  let base = v.a;
  if (k < 0) {
    const inv = inverseOf(v.a);
    if (!inv)
      throw new MatError(
        'inverse',
        'Cette matrice n\u2019est pas inversible : son d\xE9terminant est nul.',
      );
    base = inv;
    k = -k;
  }
  if (k > 4096) throw new MatError('power', 'Puissance trop grande.');
  let out = { t: 'id', s: Q.ONE };
  let b = mat(base);
  while (k > 0) {
    if (k & 1) out = mulVals(out, b);
    b = mulVals(b, b);
    k >>= 1;
  }
  return out.t === 'id' ? mat(identity(v.r, out.s)) : out;
}
function transposeVal(v) {
  if (v.t !== 'm') return v;
  return mat(Array.from({ length: v.c }, (_, j) => v.a.map((row) => row[j])));
}
function valEq(x, y) {
  if (x.t === 's' && y.t === 's') return Q.eq(x.q, y.q);
  if (x.t === 's' || y.t === 's') {
    const s = x.t === 's' ? x : y;
    const o = x.t === 's' ? y : x;
    if (!Q.isZero(s.q)) return false;
    if (o.t === 'id') return Q.isZero(o.s);
    return o.a.every((row) => row.every(Q.isZero));
  }
  if (x.t === 'id' && y.t === 'id') return Q.eq(x.s, y.s);
  if (x.t === 'm' && y.t === 'm')
    return (
      x.r === y.r && x.c === y.c && x.a.every((row, i) => row.every((a, j) => Q.eq(a, y.a[i][j])))
    );
  const m = x.t === 'm' ? x : y;
  const id = x.t === 'id' ? x : y;
  return m.r === m.c && valEq(m, mat(identity(m.r, id.s)));
}
class MatSyntaxError extends Error {}
class MatIncomplete extends Error {}
const SUP_DIGITS = '\u2070\xB9\xB2\xB3\u2074\u2075\u2076\u2077\u2078\u2079';
const SUB_DIGITS = '\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089';
const MATRIX_NAME_RE = new RegExp(`^([A-Z]|\u03C0)[${SUB_DIGITS}]*$`);
const SUP_RE = new RegExp(`^(\u207B\xB9|\u1D40|[${SUP_DIGITS}]+)$`);
const MAT_NUM_RE = /^−?\d+(,\d+)?$/;
const OPS = /* @__PURE__ */ new Set([
  '=',
  '+',
  '\u2212',
  '\xD7',
  '\xF7',
  '(',
  ')',
  '[',
  ']',
  ',',
  ';',
]);
const supToNumber = (s) => Number([...s].map((c) => SUP_DIGITS.indexOf(c)).join(''));
const numberToSup = (n) =>
  String(n)
    .split('')
    .map((d) => SUP_DIGITS[Number(d)])
    .join('');
const isSupAtom = (a) => SUP_RE.test(a);
const ENTRY_RE = new RegExp(`^[${SUB_DIGITS}]+$`);
const isEntryAtom = (a) => ENTRY_RE.test(a);
const ENTRY_TEXT_RE = new RegExp(`\\)[${SUB_DIGITS}]`);
const isMatName = (a) => MATRIX_NAME_RE.test(a);
function matLineAtoms(text) {
  const out = [];
  const s = text.trim();
  let i = 0;
  const map1 = { '-': '\u2212', '*': '\xD7', '/': '\xF7', '\xB7': '\xD7' };
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (/\d/.test(c)) {
      let j = i;
      while (j < s.length && /\d/.test(s[j])) j++;
      if (s[j] === ',' && /\d/.test(s[j + 1] ?? '')) {
        j++;
        while (j < s.length && /\d/.test(s[j])) j++;
      }
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    if (SUB_DIGITS.includes(c) && out[out.length - 1] === ')') {
      let j = i;
      while (j < s.length && SUB_DIGITS.includes(s[j])) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    if (/[A-Z]/.test(c) || c === '\u03C0') {
      let j = i + 1;
      while (j < s.length && SUB_DIGITS.includes(s[j])) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    if (s.startsWith('det', i)) {
      out.push('det');
      i += 3;
      continue;
    }
    if (s.startsWith('\u207B\xB9', i)) {
      out.push('\u207B\xB9');
      i += 2;
      continue;
    }
    if (c === '\u1D40') {
      out.push('\u1D40');
      i++;
      continue;
    }
    if (SUP_DIGITS.includes(c)) {
      let j = i;
      while (j < s.length && SUP_DIGITS.includes(s[j])) j++;
      out.push(s.slice(i, j));
      i = j;
      continue;
    }
    const op = map1[c] ?? c;
    if (!OPS.has(op))
      throw new MatSyntaxError(`\xAB ${c} \xBB ne s\u2019\xE9crit pas dans un calcul de matrices.`);
    out.push(op);
    i++;
  }
  return out;
}
function matText(atoms) {
  let out = '';
  atoms.forEach((a, i) => {
    const prev = atoms[i - 1];
    const glue =
      i === 0 ||
      isSupAtom(a) ||
      isEntryAtom(a) || // un moins devant un nombre : « −1 », « [3, −1 ; …] »
      (prev === '\u2212' &&
        (i < 2 || ['(', '[', ',', ';', '=', '\xD7', '\xF7'].includes(atoms[i - 2]))) ||
      a === ')' ||
      a === ']' ||
      a === ',' ||
      prev === '(' ||
      prev === '[' ||
      (prev === 'det' && a === '(') || // AB, 2A, TX₀, A(B + C)
      ((isMatName(a) || a === '(' || a === '[') &&
        prev !== void 0 &&
        (isMatName(prev) ||
          isSupAtom(prev) ||
          MAT_NUM_RE.test(prev) ||
          prev === ')' ||
          prev === ']'));
    out += (glue ? '' : ' ') + a;
  });
  return out;
}
function parseMatLine(A) {
  let p = 0;
  const peek = () => A[p];
  const eat = (a) => {
    if (A[p] !== a) {
      if (p >= A.length) throw new MatIncomplete('');
      throw new MatSyntaxError(`\xAB ${a} \xBB attendu.`);
    }
    p++;
  };
  const startsPrimary = (a) =>
    a !== void 0 && (isMatName(a) || a === '[' || a === '(' || a === 'det');
  function expr() {
    let l = term();
    while (peek() === '+' || peek() === '\u2212') {
      const op = A[p++];
      if (p >= A.length) throw new MatIncomplete('');
      l = { k: op === '+' ? 'add' : 'sub', l, r: term() };
    }
    return l;
  }
  function term() {
    let l = unary();
    for (;;) {
      const a = peek();
      if (a === '\xD7' || a === '\xF7') {
        p++;
        if (p >= A.length) throw new MatIncomplete('');
        const r = unary();
        l = a === '\xD7' ? { k: 'mul', l, r } : { k: 'div', l, r };
      } else if (startsPrimary(a)) {
        l = { k: 'mul', l, r: postfix(), implicit: true };
      } else return l;
    }
  }
  function unary() {
    if (peek() === '\u2212') {
      p++;
      if (p >= A.length) throw new MatIncomplete('');
      return { k: 'neg', e: unary() };
    }
    return postfix();
  }
  function postfix() {
    let e = primary();
    for (;;) {
      const a = peek();
      if (a !== void 0 && isEntryAtom(a)) {
        if (e.k !== 'paren')
          throw new MatSyntaxError(
            'Un coefficient s\u2019\xE9crit entre parenth\xE8ses : \xAB (M\xB2)\u2081\u2083 \xBB.',
          );
        const d = [...a].map((c) => SUB_DIGITS.indexOf(c));
        if (d.length !== 2 || d.some((x) => x < 1))
          throw new MatSyntaxError(
            'Un coefficient a deux indices, la ligne puis la colonne : \xAB (M\xB2)\u2081\u2083 \xBB.',
          );
        p++;
        e = { k: 'entry', e: e.e, i: d[0], j: d[1] };
        continue;
      }
      if (a === void 0 || !isSupAtom(a)) return e;
      p++;
      if (a === '\u1D40') e = { k: 'tr', e };
      else if (a === '\u207B\xB9') e = { k: 'pow', e, n: -1, text: a };
      else {
        const n = supToNumber(a);
        if (!Number.isInteger(n) || n < 0) throw new MatSyntaxError('Exposant illisible.');
        e = { k: 'pow', e, n, text: a };
      }
    }
  }
  function primary() {
    const a = A[p];
    if (a === void 0) throw new MatIncomplete('');
    if (MAT_NUM_RE.test(a)) {
      p++;
      const q = Q.parseDecimal(a.replace('\u2212', '-'));
      if (!q) throw new MatSyntaxError(`Nombre illisible : \xAB ${a} \xBB.`);
      return { k: 'num', q, text: a };
    }
    if (isMatName(a)) {
      p++;
      return { k: 'name', n: a };
    }
    if (a === 'det') {
      p++;
      eat('(');
      const e = expr();
      eat(')');
      return { k: 'det', e };
    }
    if (a === '(') {
      p++;
      const e = expr();
      eat(')');
      return { k: 'paren', e };
    }
    if (a === '[') {
      p++;
      const rows = [[]];
      for (;;) {
        rows[rows.length - 1].push(expr());
        const s = A[p];
        if (s === void 0) throw new MatIncomplete('');
        p++;
        if (s === ']') break;
        if (s === ',') continue;
        if (s === ';') {
          rows.push([]);
          continue;
        }
        throw new MatSyntaxError(
          `\xAB ${s} \xBB : dans une matrice, \xAB , \xBB s\xE9pare deux coefficients et \xAB ; \xBB deux lignes.`,
        );
      }
      const w = rows[0].length;
      if (rows.some((r) => r.length !== w))
        throw new MatSyntaxError(
          'Les lignes de la matrice n\u2019ont pas le m\xEAme nombre de coefficients.',
        );
      return { k: 'lit', rows };
    }
    throw new MatSyntaxError(`\xAB ${a} \xBB ne commence pas un calcul.`);
  }
  if (!A.length) throw new MatIncomplete('');
  const members = [expr()];
  while (p < A.length) {
    if (A[p] !== '=') throw new MatSyntaxError(`\xAB ${A[p]} \xBB de trop.`);
    p++;
    if (p >= A.length) throw new MatIncomplete('');
    members.push(expr());
  }
  return members;
}
const IDENTITY_RE = new RegExp(`^I([${SUB_DIGITS}]*)$`);
function evalM(e, env) {
  switch (e.k) {
    case 'num':
      return scal(e.q);
    case 'name': {
      const v = env.get(e.n);
      if (v) return v;
      const id = IDENTITY_RE.exec(e.n);
      if (id) {
        if (!id[1]) return { t: 'id', s: Q.ONE };
        const n = Number([...id[1]].map((c) => SUB_DIGITS.indexOf(c)).join(''));
        return mat(identity(n));
      }
      throw new MatError('unknown', `Je ne connais pas la matrice ${e.n}.`);
    }
    case 'lit': {
      const a = e.rows.map((row) =>
        row.map((x) => {
          const v = evalM(x, env);
          if (v.t !== 's') throw new MatError('entry', 'Un coefficient de matrice est un nombre.');
          return v.q;
        }),
      );
      return mat(a);
    }
    case 'paren':
      return evalM(e.e, env);
    case 'add':
      return addVals(evalM(e.l, env), evalM(e.r, env), 1);
    case 'sub':
      return addVals(evalM(e.l, env), evalM(e.r, env), -1);
    case 'neg':
      return scaleVal(evalM(e.e, env), Q.rat(-1));
    case 'mul':
      return mulVals(evalM(e.l, env), evalM(e.r, env));
    case 'div': {
      const r = evalM(e.r, env);
      if (r.t !== 's')
        throw new MatError(
          'kind',
          'On ne divise pas par une matrice : on multiplie par son inverse.',
        );
      const inv = Q.div(Q.ONE, r.q);
      if (!inv) throw new MatError('div0', 'Division par 0.');
      return scaleVal(evalM(e.l, env), inv);
    }
    case 'pow':
      return powVal(evalM(e.e, env), e.n);
    case 'tr':
      return transposeVal(evalM(e.e, env));
    case 'det': {
      const v = evalM(e.e, env);
      if (v.t !== 'm' || v.r !== v.c)
        throw new MatError('size', 'Seule une matrice carr\xE9e a un d\xE9terminant.');
      return scal(detOf(v.a));
    }
    case 'entry': {
      const v = evalM(e.e, env);
      if (v.t === 's') throw new MatError('kind', 'Un nombre n\u2019a pas de coefficient.');
      if (v.t === 'id') return scal(e.i === e.j ? v.s : Q.ZERO);
      if (e.i > v.r || e.j > v.c)
        throw new MatError(
          'entry',
          `Une matrice ${sizeText(v)} n\u2019a pas de coefficient (${e.i}, ${e.j}).`,
        );
      return scal(v.a[e.i - 1][e.j - 1]);
    }
  }
}
const ratText = (q) => {
  const neg = q.n < 0n;
  const a = neg ? Q.neg(q) : q;
  let t;
  if (a.d === 1n) t = a.n.toString();
  else {
    let d = a.d;
    let k = 0;
    while (d % 10n === 0n) {
      d /= 10n;
      k++;
    }
    let two = 0;
    let five = 0;
    let x = a.d;
    while (x % 2n === 0n) {
      x /= 2n;
      two++;
    }
    while (x % 5n === 0n) {
      x /= 5n;
      five++;
    }
    if (x === 1n) {
      const places = Math.max(two, five);
      const n = (a.n * 10n ** BigInt(places)) / a.d;
      const s = n.toString().padStart(places + 1, '0');
      t = (s.slice(0, s.length - places) + ',' + s.slice(s.length - places)).replace(/,?0+$/, '');
    } else t = `${a.n} \xF7 ${a.d}`;
    void k;
  }
  return (neg ? '\u2212' : '') + t;
};
const matRatText = ratText;
function canon(e) {
  switch (e.k) {
    case 'num':
      return ratText(e.q);
    case 'name':
      return e.n;
    case 'lit':
      return '[' + e.rows.map((r) => r.map(canon).join(',')).join(';') + ']';
    case 'paren':
      return canon(e.e);
    case 'add':
      return `(${canon(e.l)}+${canon(e.r)})`;
    case 'sub':
      return `(${canon(e.l)}-${canon(e.r)})`;
    case 'neg':
      return `-(${canon(e.e)})`;
    case 'mul':
      return '(' + factors(e).map(canon).join('*') + ')';
    case 'div':
      return `(${canon(e.l)}/${canon(e.r)})`;
    case 'pow':
      return `(${canon(e.e)})^${e.n}`;
    case 'tr':
      return `(${canon(e.e)})^T`;
    case 'det':
      return `det(${canon(e.e)})`;
    case 'entry':
      return `(${canon(e.e)})_${e.i},${e.j}`;
  }
}
function numOf(e) {
  if (e.k === 'num') return e.q;
  if (e.k === 'paren') return numOf(e.e);
  if (e.k === 'neg') {
    const q = numOf(e.e);
    return q ? Q.neg(q) : null;
  }
  return null;
}
function isNumericLit(e) {
  const x = e.k === 'paren' ? e.e : e;
  return x.k === 'lit' && x.rows.every((r) => r.every((c) => numOf(c) !== null));
}
function namesIn(e, out = /* @__PURE__ */ new Set()) {
  switch (e.k) {
    case 'name':
      out.add(e.n);
      break;
    case 'lit':
      e.rows.forEach((r) => r.forEach((c) => namesIn(c, out)));
      break;
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
      namesIn(e.l, out);
      namesIn(e.r, out);
      break;
    case 'neg':
    case 'paren':
    case 'pow':
    case 'tr':
    case 'det':
    case 'entry':
      namesIn(e.e, out);
      break;
    default:
      break;
  }
  return out;
}
function opCount(e) {
  switch (e.k) {
    case 'num':
    case 'name':
      return 0;
    case 'lit':
      return e.rows.reduce((s, r) => s + r.reduce((t, c) => t + opCount(c), 0), 0);
    case 'paren':
      return opCount(e.e);
    case 'neg':
      return numOf(e) !== null ? 0 : 1 + opCount(e.e);
    case 'add':
    case 'sub':
    case 'mul':
    case 'div':
      return 1 + opCount(e.l) + opCount(e.r);
    case 'pow':
    case 'tr':
    case 'det':
      return 1 + opCount(e.e);
    case 'entry':
      return opCount(e.e);
  }
}
const strip = (e) => (e.k === 'paren' ? strip(e.e) : e);
function factors(e) {
  const x = strip(e);
  if (x.k === 'mul') return [...factors(x.l), ...factors(x.r)];
  return [x];
}
function powerFactors(e) {
  const out = [];
  const push = (base, n) => {
    const last = out[out.length - 1];
    if (last && last.base === base) last.n += n;
    else out.push({ base, n });
  };
  let budget = 2e3;
  const single = (y) =>
    y.k === 'name' || y.k === 'lit' || (y.k === 'pow' && y.n === -1) || y.k === 'tr';
  const walk = (x) => {
    const y = strip(x);
    if (y.k === 'mul') return walk(y.l) && walk(y.r);
    if (y.k === 'pow' && y.n >= 1) {
      const inner = strip(y.e);
      if (single(inner)) {
        push(canon(inner), y.n);
        return true;
      }
      for (let i = 0; i < y.n; i++) {
        if (--budget < 0 || !walk(inner)) return false;
      }
      return true;
    }
    if (single(y)) {
      push(canon(y), 1);
      return true;
    }
    return false;
  };
  return walk(e) ? out : null;
}
function isIdentityExpr(e) {
  const x = strip(e);
  if (x.k === 'name') return IDENTITY_RE.test(x.n);
  if (x.k === 'pow') return isIdentityExpr(x.e);
  return false;
}
function termValues(e) {
  const out = [];
  const walk = (x, sign) => {
    const y = strip(x);
    if (y.k === 'add') return walk(y.l, sign) && walk(y.r, sign);
    if (y.k === 'sub') return walk(y.l, sign) && walk(y.r, -sign);
    try {
      const v = evalM(y, /* @__PURE__ */ new Map());
      if (v.t !== 's') return false;
      out.push(sign === 1 ? v.q : Q.neg(v.q));
      return true;
    } catch {
      return false;
    }
  };
  return walk(e, 1) ? out : null;
}
function sameMultiset(a, b) {
  if (a.length !== b.length) return false;
  const key = (q) => Q.ratToText(q);
  const m = /* @__PURE__ */ new Map();
  a.forEach((q) => m.set(key(q), (m.get(key(q)) ?? 0) + 1));
  for (const q of b) {
    const n = m.get(key(q)) ?? 0;
    if (!n) return false;
    m.set(key(q), n - 1);
  }
  return true;
}
export {
  ENTRY_RE,
  ENTRY_TEXT_RE,
  MATRIX_NAME_RE,
  MAT_NUM_RE,
  MatError,
  MatIncomplete,
  MatSyntaxError,
  addVals,
  canon,
  detOf,
  evalM,
  factors,
  identity,
  inverseOf,
  isEntryAtom,
  isIdentityExpr,
  isMatName,
  isNumericLit,
  isSupAtom,
  mat,
  matLineAtoms,
  matRatText,
  matText,
  mulVals,
  namesIn,
  numOf,
  numberToSup,
  opCount,
  parseMatLine,
  powVal,
  powerFactors,
  sameMultiset,
  scal,
  scaleVal,
  supToNumber,
  termValues,
  transposeVal,
  valEq,
};
